import graphql__acabd9b274a3ce59230c from "./__generated__/HeaderWithComplexMenuDesktop_platform.graphql.js";
import graphql__d7e1c535e32a418f53f3 from "./__generated__/HeaderWithComplexMenuDesktop_authenticationConfig.graphql.js";
import graphql__a9f3c2049f73dfbe87a3 from "./__generated__/HeaderWithComplexMenuDesktop_viewer.graphql.js";
import graphql__7ef6c263984913edf096 from "./__generated__/HeaderWithComplexMenuDesktop_navigationItems.graphql.js";import { useState, useCallback, useEffect, useLayoutEffect } from "react";
import { graphql, useFragment } from "react-relay";
import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";

import HeaderAuthenticatedUser from "./HeaderAuthenticatedUser";
import ButtonLink from "../Buttons/ButtonLink";
import ComplexMenuDesktopMainNavigation from "../MainNavigation/ComplexMenu/Desktop/ComplexMenuDesktopMainNavigation";
import { useFormatMessage } from "../Intl/Context";
import { intlMessages as headerIntlMessages } from "./Header";
import { useRoutesContext } from "../Routes/RoutesContext";
import LocaleSwitch from "../LocaleSwitch";
import { useWindowSize } from "../Utils/useWindowResizedEffect";
import {
  ComplexMenuContext,
  useComplexMenuProps
} from "../MainNavigation/ComplexMenu/ComplexMenuContext";
import { useAffixHeaderOffsetTopUpdater } from "../Utils/useAffixHeaderOffsetTopUpdater";

import { setComplexSubMenuEnabled } from "../../reducers/styling";

const USER_ACCOUNT_BUTTON_OFFSET_WIDTH = 170;
const LOCALE_SWITCH_OFFSET_WIDTH = 70;
const MAIN_NAVIGATION_RIGHT_PADDING = 20;

const HeaderWithComplexMenuDesktop = props => {
  const platform = useFragment(HeaderWithComplexMenuDesktop_platform, props.platform);
  const authenticationConfig = useFragment(
    HeaderWithComplexMenuDesktop_authenticationConfig,
    props.authenticationConfig
  );
  const viewer = useFragment(HeaderWithComplexMenuDesktop_viewer, props.viewer);
  const navigationItems = useFragment(
    HeaderWithComplexMenuDesktop_navigationItems,
    props.navigationItems
  );

  const dispatch = useDispatch();

  const { logos, title, logoBackgroundColor, publishedLocales, centerMainMenu } =
    platform;
  const { enableUserAccounts } = authenticationConfig;
  const displayLocaleSwitch = publishedLocales.length > 1;
  const displayRightMenu = enableUserAccounts || displayLocaleSwitch;

  const { pathFor } = useRoutesContext();
  const homePath = pathFor("home");

  const t = useFormatMessage();
  const homeLabel = t(headerIntlMessages.home);
  const menuAriaLabel = t(headerIntlMessages.mainMenu);
  const altText = [title, homeLabel].join(", ");

  const complexMenuProps = useComplexMenuProps(navigationItems);
  const { activeSubMenu } = complexMenuProps;

  const pushableContainersOffset = useSelector(
    ({ styling }) => styling.pushableContainersOffset
  );

  const [navContainer, setNavContainer] = useState();
  const [hiddenContainer, setHiddenContainer] = useState();
  const [menuInnerContainer, setMenuInnerContainer] = useState();
  const [logoContainer, setLogoContainer] = useState();
  const [mainNavigationContainer, setMainNavigationContainer] = useState();
  const [rightMenuContainer, setRightMenuContainer] = useState();
  const [displayLogoAsAbsolute, setDisplayLogoAsAbsolute] = useState(true);
  const [displayAuthenticationButtonLabel, setDisplayAuthenticationButtonLabel] =
    useState(true);
  const [mainNavigationContainerMaxWidth, setMainNavigationContainerMaxWidth] =
    useState();

  const { windowWidth } = useWindowSize();

  const calculateMenuElementsPositions = useCallback(() => {
    // If there's a missing ref container, wait for it to be available
    if (
      !hiddenContainer ||
      !menuInnerContainer ||
      !logoContainer ||
      !mainNavigationContainer ||
      (displayRightMenu && !rightMenuContainer)
    ) {
      return;
    }

    const innerNavigation = mainNavigationContainer.childNodes[0];

    if (!innerNavigation) return;

    const availableLeftWidth = hiddenContainer.getBoundingClientRect().x;
    const neededLogoWidth = logoContainer.offsetWidth;

    const innerNavigationSize = innerNavigation.getBoundingClientRect();
    const innerNavigationWidth = innerNavigationSize.width;
    const containerHasEnoughWidth = innerNavigationWidth <= hiddenContainer.offsetWidth;
    const logoHasEnoughWidth = availableLeftWidth >= neededLogoWidth;

    // When centerMainMenu is enabled, logo is always absolute regardless of space calculations
    const shouldDisplayLogoAsAbsolute =
      centerMainMenu || (containerHasEnoughWidth && logoHasEnoughWidth);

    // When there's not enough space for the logo to be displayed as absolute on the left
    // of the main navigation menu, we make it display as static so navigation items don't
    // overlap the logo image.
    setDisplayLogoAsAbsolute(prev =>
      prev === shouldDisplayLogoAsAbsolute ? prev : shouldDisplayLogoAsAbsolute
    );

    // When centerMainMenu is enabled, skip authentication button label calculations
    // as the centered layout doesn't require dynamic button label hiding
    if (!displayRightMenu || centerMainMenu) return;

    let minimumRightMenuExpandedWidth = 0;
    if (enableUserAccounts)
      minimumRightMenuExpandedWidth += USER_ACCOUNT_BUTTON_OFFSET_WIDTH;
    if (displayLocaleSwitch) minimumRightMenuExpandedWidth += LOCALE_SWITCH_OFFSET_WIDTH;

    const innerNavigationRightX = innerNavigationSize.x + innerNavigationWidth;
    const shouldDisplayAuthenticationButtonLabel =
      windowWidth - innerNavigationRightX >= minimumRightMenuExpandedWidth;

    // If there's not enough space on the right to display the expanded authentication
    // button label, we ensure that the button label is hidden to give more space to the
    // main navigation menu items display.
    setDisplayAuthenticationButtonLabel(prev =>
      prev === shouldDisplayAuthenticationButtonLabel
        ? prev
        : shouldDisplayAuthenticationButtonLabel
    );
  }, [
    centerMainMenu,
    displayLocaleSwitch,
    displayRightMenu,
    enableUserAccounts,
    hiddenContainer,
    logoContainer,
    mainNavigationContainer,
    menuInnerContainer,
    rightMenuContainer,
    windowWidth
  ]);

  useEffect(() => {
    if (!mainNavigationContainer) return;

    const innerNavigation = mainNavigationContainer.childNodes[0];

    if (!innerNavigation) return;

    const innerNavigationSize = innerNavigation.getBoundingClientRect();
    // This allows defining a max-width style to the main navigation container, in case
    // there's not enough space to display it with the full "container" width. This
    // ensures that every navigation item is always shown to the user, even with many
    // navigation items and a really small desktop screen size, until the window width is
    // small enough to trigger the layout to be rearranged to tablet mode.
    if (!displayAuthenticationButtonLabel) {
      const rightMenuContainerX = rightMenuContainer.getBoundingClientRect().x;

      setMainNavigationContainerMaxWidth(
        rightMenuContainerX - innerNavigationSize.x - MAIN_NAVIGATION_RIGHT_PADDING
      );
    } else if (displayAuthenticationButtonLabel && mainNavigationContainerMaxWidth) {
      setMainNavigationContainerMaxWidth(null);
    }
  }, [
    displayAuthenticationButtonLabel,
    mainNavigationContainer,
    mainNavigationContainerMaxWidth,
    rightMenuContainer
  ]);

  useLayoutEffect(() => {
    calculateMenuElementsPositions();
  }, [calculateMenuElementsPositions]);

  useEffect(() => {
    dispatch(setComplexSubMenuEnabled(!!activeSubMenu));
  }, [dispatch, activeSubMenu]);

  useAffixHeaderOffsetTopUpdater(() => {
    return navContainer?.offsetHeight || 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navContainer, activeSubMenu]);

  const contextValue = {
    ...complexMenuProps,
    mainNavigationIsCentered: displayLogoAsAbsolute
  };

  const logoContainerClassName = classNames(
    "navbar-brand",
    "d-flex",
    "align-items-center",
    { "position-absolute": displayLogoAsAbsolute }
  );

  const mainNavigationWrapperClassName = classNames(
    "w-100",
    "bg-white",
    "box-new-menu",
    "position-relative",
    "header-with-complex-menu-desktop-main-navigation-wrapper",
    "border-primary",
    {
      "with-open-submenu": activeSubMenu
    }
  );

  const mainContainerClassName = classNames("d-flex", "h-100", "align-items-center", {
    container: displayLogoAsAbsolute,
    "w-100": !displayLogoAsAbsolute,
    "justify-content-center": displayLogoAsAbsolute && centerMainMenu
  });

  const mainNavigationContainerClassName = classNames({
    container: displayLogoAsAbsolute || centerMainMenu,
    "margin-left-none": !displayLogoAsAbsolute
  });

  const mainNavigationStyles = mainNavigationContainerMaxWidth
    ? {
        maxWidth: mainNavigationContainerMaxWidth
      }
    : {};

  return (
    <ComplexMenuContext.Provider value={contextValue}>
      <header role="banner">
        <nav
          role="navigation"
          className="header-with-complex-menu-desktop navbar sticky-top navbar-default navbar-fixed-top bg-primary padding-top-none padding-bottom-none position-sticky margin-bottom-xs"
          data-header
          style={{ top: pushableContainersOffset }}
          ref={setNavContainer}
          aria-label={menuAriaLabel}
        >
          <div className={mainNavigationWrapperClassName}>
            <div
              className="header-with-complex-menu-desktop-inner h-100"
              ref={setMenuInnerContainer}
            >
              <div className={mainContainerClassName}>
                <ButtonLink
                  to={homePath}
                  className={logoContainerClassName}
                  style={{ backgroundColor: logoBackgroundColor }}
                  ref={setLogoContainer}
                >
                  <img
                    src={logos.header}
                    alt={altText}
                    className="navbar-brand-logo"
                    onLoad={calculateMenuElementsPositions}
                  />
                </ButtonLink>

                <div
                  className={mainNavigationContainerClassName}
                  style={mainNavigationStyles}
                  ref={setMainNavigationContainer}
                >
                  <ComplexMenuDesktopMainNavigation
                    navigationItems={navigationItems}
                    viewer={viewer}
                    authenticationConfig={authenticationConfig}
                    platform={platform}
                  />
                </div>

                {displayRightMenu && (
                  <div
                    className="header-with-complex-menu-desktop-right-menu d-flex align-items-center position-absolute"
                    ref={setRightMenuContainer}
                  >
                    <HeaderAuthenticatedUser
                      viewer={viewer}
                      authenticationConfig={authenticationConfig}
                      hideLabel={!displayAuthenticationButtonLabel}
                      withinComplexMenu
                    />

                    {displayLocaleSwitch && (
                      <LocaleSwitch platform={platform} withinComplexMenu />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
      </header>

      <div className="complex-menu-hidden-renderer w-100">
        <div className="container" ref={setHiddenContainer} />
      </div>
    </ComplexMenuContext.Provider>
  );
};

const HeaderWithComplexMenuDesktop_platform = graphql__acabd9b274a3ce59230c;

const HeaderWithComplexMenuDesktop_authenticationConfig = graphql__d7e1c535e32a418f53f3;

const HeaderWithComplexMenuDesktop_viewer = graphql__a9f3c2049f73dfbe87a3;

const HeaderWithComplexMenuDesktop_navigationItems = graphql__7ef6c263984913edf096;

export default HeaderWithComplexMenuDesktop;
